/**
 * Letters as a person sees them. A letter is not a UTF-16 unit: an
 * emoji is two units, an accent may be its own code point after the
 * letter, and a flag or a family is several code points joined. Half of
 * one draws as a box. Pure.
 */

const SEGMENTER =
    typeof Intl.Segmenter === "function"
        ? new Intl.Segmenter("en", { granularity: "grapheme" })
        : null;

/** Without a segmenter: a code point and the marks that follow it. */
const NEAR_GRAPHEME = /\P{M}\p{M}*|\p{M}+/gsu;

/** The letters of `text`, in order, as many as are asked for. */
export function letters(text: string, most = Infinity): string[] {
    const out: string[] = [];
    if (most <= 0) return out;
    for (const letter of each(text)) {
        out.push(letter);
        if (out.length >= most) break;
    }
    return out;
}

function* each(text: string): Generator<string> {
    if (SEGMENTER) {
        for (const { segment } of SEGMENTER.segment(text)) yield segment;
        return;
    }
    for (const [letter] of text.matchAll(NEAR_GRAPHEME)) yield letter;
}

/** The first letter of `text`, spaces aside; empty when there is none. */
export function firstLetter(text: string): string {
    return letters(text.trim().slice(0, 64), 1)[0] ?? "";
}

/**
 * A letter as a capital, when its capital is one letter too. `ß` is `SS`
 * and `ﬁ` is `FI` as capitals, two letters where an initial is one, so
 * those stay as they are.
 */
export function capital(letter: string): string {
    const upper = letter.toUpperCase();
    return letters(upper, 2).length === 1 ? upper : letter;
}

/** The longest start of `text` no longer than `max` UTF-16 units that ends on a whole letter. */
export function cutAt(text: string, max: number): string {
    if (text.length <= max) return text;
    let out = "";
    for (const letter of letters(text.slice(0, max + 64))) {
        if (out.length + letter.length > max) break;
        out += letter;
    }
    return out;
}
